/**
 * What the like-with-like filter left out of a comparison, in one line
 * — and, since CU-869fh646w, what it did not filter at all.
 *
 * Pure, so it is tested without mounting anything and used from two
 * places that cannot share a component: the footer of the competitors
 * popup, and the Reason block when there is no popup to open because
 * the filter left nothing to compare against (UX gate, 2026-10-07).
 *
 * The filter judges our own row's kind — the row the engine took, not
 * the rate card's axes (Curro, 2026-10-14) — and a strategy can switch
 * an axis off ("Compare against every tariff type / insurance"). The
 * event records the two switches as `derivation.compare =
 * {every_tariff_type, every_insurance}`; with an axis off its counter
 * is 0 by construction and the sentence says why instead of keeping
 * quiet (brief criterion 9). An event written before the switches
 * existed carries no `compare` and keeps the sentence it always had.
 *
 * Each half is dropped at zero; both at zero, or an event written before
 * the block existed, says nothing — "0 excluded" is noise.
 */

const n = (v) => Number(v || 0)
const offers = (k) => `${k} offer${k === 1 ? '' : 's'}`

/** The counters, named for the axes that still filter. */
function leftOut(sf, { tariffOn, insuranceOn }) {
	if (!sf) return null
	const m = n(sf.excluded_face_mismatch), u = n(sf.excluded_face_unknown)
	const axis = tariffOn ? 'insurance' : insuranceOn ? 'tariff type' : 'insurance or tariff type'
	const parts = []
	if (m > 0) parts.push(`${offers(m)} on a different ${axis}`)
	if (u > 0) parts.push(`${offers(u)} with no ${axis} recorded`)
	return parts.length ? `Left out of this comparison: ${parts.join(' · ')}.` : null
}

export function excludedCopy(sf, compare = null) {
	const tariffOn = !!compare?.every_tariff_type
	const insuranceOn = !!compare?.every_insurance
	if (tariffOn && insuranceOn) {
		return 'Neither tariff type nor insurance filtered — this strategy compares against every kind of offer.'
	}
	const counters = leftOut(sf, { tariffOn, insuranceOn })
	if (tariffOn) {
		return `Tariff type not filtered — this strategy compares against every tariff type.${counters ? ` ${counters}` : ''}`
	}
	if (insuranceOn) {
		return `Insurance not filtered — this strategy compares against every insurance.${counters ? ` ${counters}` : ''}`
	}
	return counters
}

/**
 * The subtitle of the competitors popup: who counted as a competitor.
 *
 * `own` is the row the engine took (`derivation.own`, new events),
 * `sf` the `scope_filter` block (its kind is the row taken too, and the
 * only source on events between 2026-10-08 and this task). Without
 * `compare` the sentence is the one those events always had. Returns
 * `null` when the event records no kind at all.
 */
export function compareCopy(sf, own, compare, names) {
	const tariff = own && own.tariff_type != null ? Boolean(Number(own.tariff_type))
	             : sf && sf.is_prepaid != null ? Boolean(Number(sf.is_prepaid)) : null
	const insurance = own && own.insurance != null ? own.insurance
	                : sf ? sf.insurance : null
	if (!compare) {
		if (!sf) return null
		return `same insurance and tariff type as our rate card: ${names.insuranceName(insurance)} · ${names.prepaidName(tariff)}`
	}
	const tariffOn = !!compare.every_tariff_type
	const insuranceOn = !!compare.every_insurance
	if (tariffOn && insuranceOn) return 'every tariff type and insurance'
	if (tariffOn) return `every tariff type · same insurance as our own row: ${names.insuranceName(insurance)}`
	if (insuranceOn) return `every insurance · same tariff type as our own row: ${names.prepaidName(tariff)}`
	return `same tariff type and insurance as our own row: ${names.prepaidName(tariff)} · ${names.insuranceName(insurance)}`
}

/** The tooltip that goes with it — both sentences the UX gate wrote: what the
 *  counters count, and the one decision an operator would not guess. */
export const EXCLUDED_TOOLTIP = "Offers that passed the strategy's market scope but not our own row's "
	+ 'insurance and tariff type. Partially prepaid offers count as a different tariff type.'
